import { BusinessFileType } from "./businessFile.interface";
import type {
  IdentificationAttachment,
  PersonIdentityDocumentType,
} from "./businessFull.interface";

/**
 * Field names follow the handler that owns this contract:
 * `shared/services/adyen-service/{buildAdyenDocumentState,resubmitAdyenDocument}.ts`.
 */

/** Which Adyen legal entity owns the document. Drives whether personId applies. */
export type AdyenDocumentOwnerKind = "business" | "person";

export type AdyenDocumentPageType = "front" | "back";

/** The Adyen LEM document types this surface can encounter. */
export type AdyenDocumentType =
  | "registrationDocument"
  | "bankStatement"
  | "proofOfAddress"
  | "vatDocument"
  | "proofOfOrganizationTaxInfo"
  | "proofOfOwnership"
  | "proofOfIndustry"
  | "proofOfSignatory"
  | PersonIdentityDocumentType;

/** Which source won the race to supply the bytes a submission would send. */
export type AdyenDocumentSourceWon =
  "idv" | "url" | "admin" | "additionalFiles";

export type AdyenDocumentEligibility =
  "submittable" | "submittable_with_acknowledgement" | "not_submittable";

/**
 * Machine code plus operator-facing copy. The read path reports a lowercase
 * reason and the submit path throws its uppercase form, so codes are compared
 * through `normalizeAdyenDocumentReasonCode`.
 */
export type AdyenDocumentReason = {
  code: string;
  message?: string;
};

/** A verification problem Adyen raised. `code` is absent on some deliveries. */
export type AdyenDocumentBlocker = {
  type?: string;
  code?: string;
  message?: string;
  entityId?: string;
  entityType?: string;
};

/** `uploaded` is a document we submitted; `downloaded` is one we synced from Adyen. */
export type AdyenDocumentOrigin = "uploaded" | "downloaded";

/** The newest document Adyen holds for this slot. Null means it holds none. */
export type AdyenMirroredDocument = {
  id: string;
  type?: string;
  modificationDate?: string;
  fileName?: string;
  /** Absent on a backend that predates it. */
  origin?: AdyenDocumentOrigin;
};

/** The file resubmission would actually pick, described for the operator. */
export type AdyenDocumentSourceFile = IdentificationAttachment & {
  uploadedAt?: string;
};

export type AdyenDocumentAcknowledgement = "approvedOwner" | "merchantDocument";

/** One row per document type and owner. */
export type AdyenDocumentStateRow = {
  documentType: BusinessFileType;
  ownerKind: AdyenDocumentOwnerKind;
  personId?: string;
  ownerId?: string;
  /** Shared by every row that resubmits together. */
  submissionKey?: string;
  adyenDocument: AdyenMirroredDocument | null;
  declaredType?: PersonIdentityDocumentType;
  detectedType?: PersonIdentityDocumentType;
  /** The Adyen type a submission would declare. */
  typeToSend?: AdyenDocumentType;
  hasUsableBytes: boolean;
  sourceWon?: AdyenDocumentSourceWon;
  /** Sides the winning source yields, in submission order. */
  sidesToSubmit: AdyenDocumentPageType[];
  /** True when this row's own side is one of them. */
  sideHeld: boolean;
  sourceRef?: string;
  file?: AdyenDocumentSourceFile;
  isAdyenSourced: boolean;
  blockers: AdyenDocumentBlocker[];
  /** False when a legacy unscoped problem might belong to this owner. */
  blockersAttributable: boolean;
  eligibility: AdyenDocumentEligibility;
  /** Why, whenever the verdict is not a plain `submittable`. */
  reason?: string;
};

export type GetAdyenDocumentStateResponse = {
  businessId: string;
  legalEntityId?: string;
  onboardingStatus?: string;
  periodicReviewPending: boolean;
  rows: AdyenDocumentStateRow[];
};

/**
 * The operator is deliberately absent: the endpoint attributes the submission
 * from the caller's id token, so a body-supplied one is ignored.
 */
export type ResubmitAdyenDocumentRequest = {
  businessId: string;
  documentType: BusinessFileType;
  personId?: string;
  expectedSourceRef?: string;
  acknowledgeApproved?: boolean;
  acknowledgeMerchantDocument?: boolean;
};

export type ResubmitAdyenDocumentResponse = {
  outcome: "submitted" | "unchanged";
  documentId: string;
  previousDocumentId: string | null;
  declaredType: AdyenDocumentType;
  supersededDocumentIds: string[];
  sidesSubmitted: AdyenDocumentPageType[];
  missingSide: AdyenDocumentPageType | null;
  verificationPending: true;
};

/**
 * Codes KBM branches on rather than merely displays, in the read path's
 * lowercase form. `staleSourceRef` and `wouldDeactivateMerchantDocument` exist
 * only on the submit path; `ownerApproved` only on the read path.
 */
export const ADYEN_DOCUMENT_REASON = {
  staleSourceRef: "source_ref_stale",
  ownerApproved: "owner_approved",
  ownerAlreadyApproved: "owner_already_approved",
  ownerScopeNotPersisted: "owner_scope_not_persisted",
  wouldDeactivateMerchantDocument: "would_deactivate_merchant_document",
  adyenSourcedBytes: "adyen_sourced_bytes",
  /** Synthesised from a 401, not a code the endpoints emit. */
  sessionExpired: "session_expired",
} as const;

/**
 * The read path reports `back_only`; the submit path throws `BACK_ONLY` for the
 * same refusal. Comparing either against a code needs one of them normalised.
 */
export function normalizeAdyenDocumentReasonCode(code?: string): string {
  return (code ?? "").toLowerCase();
}

/**
 * Operator-facing copy, keyed on the normalised code. Preferred over the
 * backend's own message, which names internal fields rather than the next step.
 */
/** The read path and the submit path spell these two refusals differently. */
const OWNER_APPROVED_COPY =
  "Adyen has already approved this owner. Submitting can move a capability backwards.";
const NO_LEGAL_ENTITY_COPY = "No Adyen legal entity exists for this owner yet.";

export const ADYEN_DOCUMENT_REASON_COPY: Record<string, string> = {
  [ADYEN_DOCUMENT_REASON.staleSourceRef]:
    "A newer file was uploaded after this page loaded. Reload before submitting.",
  [ADYEN_DOCUMENT_REASON.ownerApproved]: OWNER_APPROVED_COPY,
  [ADYEN_DOCUMENT_REASON.ownerAlreadyApproved]: OWNER_APPROVED_COPY,
  [ADYEN_DOCUMENT_REASON.ownerScopeNotPersisted]:
    "No per-owner problem scope is stored for this merchant yet, so approval cannot be ruled out.",
  [ADYEN_DOCUMENT_REASON.wouldDeactivateMerchantDocument]:
    "This would deactivate the document the merchant supplied through hosted onboarding.",
  [ADYEN_DOCUMENT_REASON.sessionExpired]:
    "Your session has expired. Reload the page and sign in again.",
  no_identity_document_source: "No identity document source is on file.",
  no_idv_attachments: "No IDV file is stored for this merchant.",
  no_matching_cluster:
    "No IDV capture close enough to this person's verification to attribute.",
  back_only: "Only a back side is on file. Upload the front before submitting.",
  adyen_sourced_bytes:
    "This file came from Adyen, so resubmitting it would change nothing.",
  no_durable_source: "No file in our own storage to send.",
  no_registration_document: "No registration document is on file.",
  sole_trader_multiple_persons:
    "Sole trader with more than one person: which person the legal entity represents is ambiguous.",
  not_an_identity_document:
    "The file reads as a residence document. Collect a passport, identity card or driving licence.",
  no_legal_entity: NO_LEGAL_ENTITY_COPY,
  adyen_legal_entity_missing: NO_LEGAL_ENTITY_COPY,
  unsupported_document_type:
    "Adyen accepts only .pdf, .jpg, .jpeg and .png. Re-upload the document in one of those formats.",
  invalid_business_document_url:
    "The stored file location is not readable. Re-upload the document.",
  empty_document_file: "The stored file is empty. Re-upload the document.",
  placeholder_document_file:
    "This is a placeholder image, not a real document. Collect the actual file from the merchant.",
  blank_document_image:
    "The image is blank. Collect a legible scan from the merchant.",
  person_id_required: "This document type needs a person.",
  person_id_not_allowed:
    "This document type is owned by the business, not a person.",
  person_not_found: "That person is no longer on this business.",
  business_not_found: "That business no longer exists.",
  adyen_document_upload_invalid_response:
    "Adyen accepted the upload but returned no document id. Check Adyen before retrying.",
  adyen_document_read_back_mismatch:
    "Adyen accepted the upload but did not report it back correctly. Check Adyen before retrying.",
  adyen_document_not_active:
    "Adyen stored the document but has not made it active. Check Adyen before retrying.",

  // Blockers carry Adyen's own verification codes, not the refusal codes above.
  "2_902":
    "The merchant has not accepted the Adyen terms of service. No document upload resolves this — they must complete the hosted onboarding link.",

  adminonly: "You are not signed in as a LemonPay admin.",
  faultyparams: "That request was rejected as malformed. Reload the page.",
};

export function describeAdyenDocumentReason(
  reason: AdyenDocumentReason | AdyenDocumentBlocker
): string {
  const code = normalizeAdyenDocumentReasonCode(reason.code);
  return (
    ADYEN_DOCUMENT_REASON_COPY[code] ||
    reason.message ||
    reason.code ||
    "No reason was given."
  );
}
